import GridTable from '@nadavshaar/react-grid-table';
import { useBlockLayout, useSortBy, useTable } from 'react-table'
import { MdModeEdit } from 'react-icons/md';
import React, { useEffect, useMemo, useState } from 'react';
import scrollbarWidth from '../../core/utils/scrollbarWidth';
import { FixedSizeList } from 'react-window';
import { CellAssistanceRenderer } from '../BarChart';
import { PercentCellRenderer } from '../Card';
import { RiArrowDownLine, RiArrowUpLine } from 'react-icons/ri';
import { formatHeatMapDataTable } from '../AreaChart';
import { Popover } from 'antd';
import { isJson } from '../../core/utils/jsonVerify';
import "./styles.scss"
import dayjs from 'dayjs';

const gridTexts = {
  search: 'Pesquisar:',
  totalRows: 'Total de linhas:',
  rows: 'Linhas:',
  selected: 'Selecionados',
  rowsPerPage: 'Linhas por página:',
  page: 'Página:',
  of: 'de',
  prev: 'Anterior',
  next: 'Próxima',
  columnVisibility: 'Visibilidade da coluna'
};

export function formatToDDMMYYYY(inputDate) {
  // Se já é uma string no formato DD/MM/YYYY, retornar como está
  if (typeof inputDate === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(inputDate)) {
    return inputDate;
  }

  // Se é um objeto com propriedade 'value' (formato comum no sistema)
  let dateValue = inputDate;
  if (inputDate && typeof inputDate === 'object' && inputDate.value) {
    dateValue = inputDate.value;
  }

  // Se é um objeto dayjs com propriedade $d
  let date;
  if (dateValue?.$d) {
    date = dayjs(dateValue.$d);
  } else {
    // Tenta processar diretamente com dayjs
    date = dayjs(dateValue);
  }

  if (!date.isValid()) {
    console.error('Data inválida:', inputDate);
    return typeof inputDate === 'string' ? inputDate : JSON.stringify(inputDate);
  }

  return date.format('DD/MM/YYYY');
}

export const ActionsCell = ({ data, openModalUpdate }) => {
  return (
    <div
      className="rgt-cell-inner"
      style={{ display: "flex", alignItems: "center", justifyContent: 'center', overflow: "hidden" }}>
      <button className="rgt-cell-inner-actions" onClick={() => openModalUpdate(data)}>
        <MdModeEdit />
      </button>
    </div>
  )
}

export const OnRowClickCell = ({
  data,
  value,
  goToDetails
}) => {
  return (
    <div
      className="rgt-cell-inner"
      style={{ display: "flex", alignItems: "center", overflow: "hidden" }} onClick={() => goToDetails(data?.id)}>
      {value}
    </div>
  )
}

export const OnClickColumns = ({
  value, column, filterByColumns
}) => {
  return (
    <div
      className="rgt-cell-inner"
      style={{ display: "flex", alignItems: "center", overflow: "hidden" }} onClick={() => filterByColumns(column?.field, value)}>
      {value}
    </div>
  )
}

export const Grid = (props) => {
  return <GridTable texts={gridTexts} {...props} />
}

export const Table = ({
  data,
  lineCaptions,
  title,
  hasBackground,
  fontSize = "2rem",
  width = "80%",
  hasPaddingLeft,
}) => {
  return (
    <div className="table__container">
      <div className="table__title" style={{ fontSize: fontSize }}>
        {title}
      </div>
      <table className="table__list" style={{ width: width }}>
        <tbody>
          {data &&
            Object.values(data).map((item, index) => {
              return (
                <tr className={`${hasBackground ? "tr--gray" : "tr--white"}`} key={index}>
                  <td className={`${hasPaddingLeft && "td--paddingLeft"}`}>
                    {lineCaptions[index] || ""}
                  </td>
                  <td style={{ textAlign: "left" }}>{item}</td>
                </tr>
              )
            })}
        </tbody>
      </table>
    </div>
  )
}



export const TableVirtualized = ({ data, onFilter, distinct = false }) => {
  console.log('🔧 TABLE VIRTUALIZED:', { data, distinct, fieldsCount: data?.fields?.length });
  
  // Decidir se agrupa por Produtor ou por Atendimento
  const unifiedFields = {}
  
  data.fields.forEach(field => {
    let key;

    field.Atendimento = field.ID.split(field.ProdutorId)[0]
    
    if (distinct && field.Atendimento) {
      // Agrupar por Atendimento quando distinct=true
      key = `atendimento_${field.Atendimento}`;
      console.log('🔗 Agrupando por atendimento:', field.Atendimento);
    } else {
      // Agrupar por Produtor (comportamento original)
      const producerId = field.ID || field.id
      const producerName = field.Produtor || field.produtor
      key = `${producerId}_${producerName}`;
    }
    
    const dateField = field.Inicio || field.inicio || field.data || field.Data
    
    if (!unifiedFields[key]) {
      // Primeira ocorrência - adicionar todos os dados
      unifiedFields[key] = { ...field }
      console.log('✅ Primeira ocorrência:', key);
    } else {
      // Já existe - combinar dados, priorizando valores mais recentes e não vazios
      const existing = unifiedFields[key]
      const existingDate = new Date(existing.Inicio || existing.inicio || existing.data || existing.Data || '1900-01-01')
      const currentDate = new Date(dateField || '1900-01-01')
      
      console.log('🔄 Combinando dados para:', key);
      
      // Para cada campo, usar o valor mais recente que não seja vazio/null/undefined
      Object.keys(field).forEach(columnKey => {
        const currentValue = field[columnKey]
        const existingValue = existing[columnKey]
        
        // Se o valor atual não está vazio e (não existe valor anterior OU o atual é mais recente)
        if (currentValue !== null && currentValue !== undefined && currentValue !== '' && 
            (existingValue === null || existingValue === undefined || existingValue === '' || currentDate >= existingDate)) {
          existing[columnKey] = currentValue
        }
      })
      
      // Sempre manter a data mais recente
      if (currentDate > existingDate) {
        existing.Inicio = field.Inicio || existing.Inicio
        existing.inicio = field.inicio || existing.inicio
        existing.data = field.data || existing.data
        existing.Data = field.Data || existing.Data
      }
    }
  })
  
  // Converter de volta para array
  const unifiedFieldsArray = Object.values(unifiedFields)
  
  let newFields = []
  unifiedFieldsArray.map(field => {
    // Para tabelas, vamos simplificar e não expandir arrays - apenas pegar o primeiro/último valor
    // Isso evita duplicatas desnecessárias na visualização de tabelas
    
    const processedField = { ...field }
    
    // Para cada coluna, se for array, pegar apenas o último valor (mais recente)
    data.columns.forEach(column => {
      if (Array.isArray(field[column]) && field[column].length > 0) {
        const lastValue = field[column][field[column].length - 1]
        const isDateColumn = ["DATA", "DATE", "INICIO", "FIM"].some(word => 
          column.toUpperCase().includes(word)
        );

        console.log({lastValue})
        
        if (isDateColumn) {
          const formattedDate = formatToDDMMYYYY(lastValue);
          processedField[column] = formattedDate || lastValue; // Fallback para valor original
        } else {
          processedField[column] = lastValue;
        }
      } else {
        const value = field[column]
        const isDateColumn = ["DATA", "DATE", "INICIO", "FIM"].some(word => 
          column.toUpperCase().includes(word)
        );
        
        if (isDateColumn) {
          const formattedDate = formatToDDMMYYYY(value);
          processedField[column] = formattedDate || value; // Fallback para valor original
        } else {
          processedField[column] = value;
        }
      }
    })
    
    newFields.push(processedField)
  })


  const formatColumns = data.columns.map((column, index) => {
    const hasTitle = data.codes && data.codes.find((item) => item.code === column)

    return {
      id: index,
      label: hasTitle ? hasTitle.title : column,
      field: column,
      width: data.columns?.width || "400px",
      cellRenderer: (props) => <OnClickColumns {...props} filterByColumns={onFilter} />,
    }
  })

  return <Grid rows={newFields} columns={formatColumns} />
}

export const StepVariationTable = ({ data }) => {

  const columns = useMemo(
    () => [
      {
        Header: 'Tema do Atendimento',
        width: "400",
        columns: [
          {
            Header: '',
            accessor: 'name',
            width: "400",
            Cell: (info) => <CellAssistanceRenderer {...{ info }} />
          }
        ],
      },
      {
        Header: 'Nº de atendimentos sem interação',
        width: "495",
        columns: [
          {
            Header: 'Periodo Anterior',
            accessor: 'Período anterior: Nº de atendimentos sem interação',
            width: "165",
            Cell: (info) => <CellAssistanceRenderer {...{ info }} />
          },
          {
            Header: 'Periodo Atual',
            accessor: 'Período atual: Nº de atendimentos sem interação',
            width: "165",
            Cell: (info) => <CellAssistanceRenderer {...{ info }} />
          },
          {
            Header: 'Variação',
            accessor: 'Var: Sem interação',
            width: "165",
            Cell: (info) => <PercentCellRenderer {...{ info }} />
          },
        ],
      },
      {
        Header: 'Nº de atendimentos com Interação',
        width: "495",
        columns: [
          {
            Header: 'Periodo Anterior',
            accessor: 'Período anterior: Nº de atendimentos com Interação',
            width: "165",
            Cell: (info) => <CellAssistanceRenderer {...{ info }} />
          },
          {
            Header: 'Periodo Atual',
            accessor: 'Período atual: Nº de atendimentos com Interação',
            width: "165",
            Cell: (info) => <CellAssistanceRenderer {...{ info }} />
          },
          {
            Header: 'Variação',
            accessor: 'Var: Interação',
            width: "165",
            Cell: (info) => <PercentCellRenderer {...{ info }} />
          },
        ],
      },
      {
        Header: 'Nº de recomendações aplicadas de forma parcial',
        width: "500",
        columns: [
          {
            Header: 'Periodo Anterior',
            accessor: 'Período anterior: Nº de recomendações aplicadas de forma parcial',
            width: "165",
            Cell: (info) => <CellAssistanceRenderer {...{ info }} />
          },
          {
            Header: 'Periodo Atual',
            accessor: 'Período atual: Nº de recomendações aplicadas de forma parcial',
            width: "165",
            Cell: (info) => <CellAssistanceRenderer {...{ info }} />
          },
          {
            Header: 'Variação',
            accessor: 'Var: Aplicação Parcial',
            width: "165",
            Cell: (info) => <PercentCellRenderer {...{ info }} />
          },
        ],
      },
      {
        Header: 'Nº de recomendações aplicadas de forma Total',
        width: "500",
        columns: [
          {
            Header: 'Periodo Anterior',
            accessor: 'Período anterior: Nº de recomendações aplicadas de forma total',
            width: "165",
            Cell: (info) => <CellAssistanceRenderer {...{ info }} />
          },
          {
            Header: 'Periodo Atual',
            accessor: 'Período atual: Nº de recomendações aplicadas de forma total',
            width: "165",
            Cell: (info) => <CellAssistanceRenderer {...{ info }} />
          },
          {
            Header: 'Variação',
            accessor: 'Var: Aplicação Total',
            width: "165",
            Cell: (info) => <PercentCellRenderer {...{ info }} />
          },
        ],
      },
    ],
    []
  )

  const defaultColumn = React.useMemo(
    () => ({
      width: 150,
    }),
    []
  )

  const scrollBarSize = React.useMemo(() => scrollbarWidth(), [])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    totalColumnsWidth,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useBlockLayout,
    useSortBy
  )

  const generateHeaderStyles = (column) => {
    return {
      style: {
        minWidth: column.width,
        width: column.width,
      },
    }
  }

  const combinedHeaderProps = (defaultHeaderProps, { column }) => {
    return [defaultHeaderProps, generateHeaderStyles(column), column.getSortByToggleProps()]
  }

  const RenderRow = React.useCallback(
    ({ index, style }) => {
      const row = rows[index]
      prepareRow(row)
      return (
        <div
          {...row.getRowProps({
            style,
          })}
          className="tr custom-row"
        >
          {row.cells.map(cell => {
            return (
              <div {...cell.getCellProps()} className="td">
                {cell.render('Cell')}
              </div>
            )
          })}
        </div>
      )
    },
    [prepareRow, rows]
  )

  return (
    <div className="table-wrapper">
      <div {...getTableProps()} className="table">
        <>
          {headerGroups.map((headerGroup) => (
            <div {...headerGroup.getHeaderGroupProps()} className="tr column-header">
              {headerGroup.headers.map((column) => (
                <div
                  {...column.getHeaderProps(combinedHeaderProps)}
                  className={`th ${column.isSorted ? (column.isSortedDesc ? "desc" : "asc") : ""}`}>
                  {column.render("Header")}
                  {column.isSorted ? (
                    column.isSortedDesc ? (
                      <RiArrowDownLine />
                    ) : (
                      <RiArrowUpLine />
                    )
                  ) : (
                    ""
                  )}
                </div>
              ))}
            </div>
          ))}
        </>

        <div {...getTableBodyProps()} className="row-group">
          <FixedSizeList
            height={400}
            itemCount={rows.length}
            itemSize={35}
            width={totalColumnsWidth + scrollBarSize}>
            {RenderRow}
          </FixedSizeList>
        </div>
      </div>
    </div>
  )

}

const TooltipTable = ({ content, value, style }) => (
  <Popover content={content}>
    <div style={style}></div>
  </Popover>
);

export const CellHeatMapRenderer = ({ info, variables }) => {
  const { value, row } = info;

  const variablesToShow = variables ? Object.keys(variables)
    .map(row => `${row}: ${variables[row]}`) : [];

  const colorMap = [
    { value: 0, color: "#ff0101" }, //
    { value: 5, color: "#ff1a1a" }, //
    { value: 10, color: "#ff3333" }, //
    { value: 15, color: "#ff4d4d" }, //
    { value: 20, color: "#ff8c02" }, //
    { value: 25, color: "#FF981C" }, //
    { value: 30, color: "#FFA436" },
    { value: 35, color: "#FFAF4F" },
    { value: 40, color: "#FFCC03" },
    { value: 45, color: "#FFD11C" },
    { value: 50, color: "#FFDB4F" },
    { value: 55, color: "#FFE069" },
    { value: 60, color: "#74A691" },
    { value: 65, color: "#64A68A" },
    { value: 70, color: "#53A683" },
    { value: 75, color: "#42A67C" },
    { value: 80, color: "#5CBE95" },
    { value: 85, color: "#3AA87A" },
    { value: 90, color: "#259969" },
    { value: 95, color: "#168758" },
    { value: 100, color: "#0b7348" },
  ];

  const currentColor = colorMap.find((row, idx) => {
    if (idx < colorMap.length - 1) {
      return value >= row.value && value < colorMap[idx + 1]?.value
    }
    return row.value >= value
  });

  const content = (
    <div>
      <div className="cell-user__container" style={{ marginBottom: "16px" }}>
        <p>Usuário: {row?.original?.user?.split(", ")[0]}</p>
        <small>{row?.original?.user?.split(", ")[1]}</small>
      </div>
      {Array.isArray(variablesToShow) &&
        variablesToShow.reverse().map((row) => (
          <div className="list-item">
            <small style={{ display: "flex", flexDirection: "column", margin: "4px 0px" }}>
              {!row.split(":")[0].includes("METODO") ? (
                <span>
                  {
                    row.split(":")[0].includes("UNIDADE") ? <><strong>UNIDADE:</strong> {row.split(":")[1] || ""}</> : <><strong>Valor:</strong> {row.split(":")[1] || ""}</>
                  }
                </span>
              ) : (
                <span>
                  <strong>Método:</strong> {row.split(":")[1]}
                </span>
              )}
            </small>
          </div>
        ))}
    </div>
  )

  return (
    <TooltipTable content={content} value={value} style={{
      height: "100%",
      backgroundColor: currentColor?.color || '#FF0101',
      textAlign: "center",
      padding: 4,
      color: "#FFF",
      cursor: "pointer"
    }} />
  )
}

export const HeatMapTable = ({ data, filter, allHeaderVariables = {} }) => {
  const [chartData, setChartData] = useState([]);

  const userColumn = {
    Header: ' ',
    width: "280",
    columns: [
      {
        Header: '',
        id: "user",
        accessor: 'user',
        width: "280",
        Cell: (info) => <div className='cell-user__container'>
          <p>{info?.value.split(', ')[0]}</p>
          <small>{info?.value.split(', ')[1]}</small>
        </div>
      }
    ],
  }

  const columnsBeforefilter = [
    userColumn,
    {
      id: 'Químico',
      Header:
        <div className="header-topic">
          <div className='separator-topic'>
            Químico
          </div>
        </div>,
      width: "500",
      columns: [
        {
          Header: () => <p>
            pH (%)
          </p>,
          id: "pH (%)",
          accessor: 'values[0].pH - QUÍMICO',
          width: "50",
          height: "50",
          Cell: (info) => <CellHeatMapRenderer {...{ info, variables: allHeaderVariables['pH'] }} />
        },
        {
          Header: () => <p>m (%)</p>,
          id: "m (%)",
          accessor: 'values[1].m (%) - QUÍMICO',
          width: "50",
          height: "50",
          Cell: (info) => <CellHeatMapRenderer {...{ info, variables: allHeaderVariables['m (%)'] }} />
        },
        {
          Header: () => <p>Zn (mg/dm3)</p>,
          id: "Zn (mg/dm3)",
          accessor: 'values[2].Zn (mg/dm3) - QUÍMICO',
          width: "50",
          height: "50",
          Cell: (info) => <CellHeatMapRenderer {...{ info, variables: allHeaderVariables['Zn (mg/dm3)'] }} />
        },
        {
          Header: () => <p>V (%)</p>,
          id: "V (%)",
          accessor: 'values[3].V (%)  - QUÍMICO',
          width: "50",
          height: "50",
          Cell: (info) => <CellHeatMapRenderer {...{ info, variables: allHeaderVariables['V (%)'] }} />
        },
        {
          Header: () => <p>S (mg/dm3)</p>,
          id: "S (mg/dm3)",
          accessor: 'values[4].S (mg/dm3) - QUÍMICO',
          width: "50",
          height: "50",
          Cell: (info) => <CellHeatMapRenderer {...{ info, variables: allHeaderVariables['S (mg/dm3)'] }} />
        },
        {
          Header: () => <p>P (mg/dm3)</p>,
          id: "P (mg/dm3)",
          accessor: 'values[5].P (mg/dm3) - QUÍMICO',
          width: "50",
          height: "50",
          Cell: (info) => <CellHeatMapRenderer {...{ info, variables: allHeaderVariables['P (mg/dm3)'] }} />
        },
        {
          Header: () => <p>Mn (mg/dm3)</p>,
          id: "Mn (mg/dm3)",
          accessor: 'values[6].Mn (mg/dm3) - QUÍMICO',
          width: "50",
          height: "50",
          Cell: (info) => <CellHeatMapRenderer {...{ info, variables: allHeaderVariables['Mn (mg/dm3)'] }} />
        },
        {
          Header: () => <p>MO (dag/kg ou %)</p>,
          id: "MO (dag/kg ou %)",
          accessor: 'values[7].MO (dag/kg ou %) - QUÍMICO',
          width: "50",
          height: "50",
          Cell: (info) => <CellHeatMapRenderer {...{ info, variables: allHeaderVariables['MO (dag/kg ou %)'] }} />
        },
        {
          Header: () => <p>Cu (mg/dm3)</p>,
          id: "Cu (mg/dm3)",
          accessor: 'values[8].Cu (mg/dm3) - QUÍMICO',
          width: "50",
          height: "50",
          Cell: (info) => <CellHeatMapRenderer {...{ info, variables: allHeaderVariables['Cu (mg/dm3)'] }} />
        },
        {
          Header: () => <p>B (mg/dm3)</p>,
          id: "B (mg/dm3)",
          accessor: 'values[9].B (mg/dm3) - QUÍMICO',
          width: "50",
          height: "50",
          Cell: (info) => <CellHeatMapRenderer {...{ info, variables: allHeaderVariables['B (mg/dm3)'] }} />
        }
      ],
    },
    {
      Header: '    ',
      width: "50",

      columns: [
        {
          Header: '        ',
          id: "separator",
          accessor: '...',
          width: "50"
        }
      ],
    },
    {
      id: 'Biológico',
      Header:
        <div className="header-topic">
          <div className='separator-topic2'>
            Biológico
          </div>
        </div>,
      width: "164",
      columns: [
        {
          Header: () => <p>Div. Taxonômica</p>,
          id: "IQS_tax",
          accessor: 'values[10].IQS_tax - BIOLÓGICO',
          width: "50",
          height: "50",
          Cell: (info) => <CellHeatMapRenderer {...{ info, variables: allHeaderVariables['IQS_tax'] }} />
        },
        {
          Header: () => <p>Perfil Bioquímico</p>,
          id: "IQS_Ec",
          accessor: 'values[11].IQS_Ec - BIOLÓGICO',
          width: "50",
          height: "50",
          Cell: (info) => <CellHeatMapRenderer {...{ info, variables: allHeaderVariables['IQS_Ec'] }} />
        }
        ,
        {
          Header: () => <p>Gêneros Func.</p>,
          id: "GF_total",
          accessor: 'values[12].GF_total - BIOLÓGICO',
          width: "50",
          height: "50",
          Cell: (info) => <CellHeatMapRenderer {...{ info, variables: allHeaderVariables['GF_total'] }} />
        }
      ],
    }
  ]

  const [columnsData, setColumnsData] = useState(columnsBeforefilter || [])

  useEffect(() => {
    if (data && data.length) {
      formatHeatMapDataTable(data, setChartData);
    }

    return () => setChartData([])
  }, [data])

  useEffect(() => {
    if (filter && filter.length) {
      const keyFiltered = (id) => [...filter, "separator"].some((keyword) => id?.includes(keyword));

      const filterColumns = (columns) => columns.filter((column) => keyFiltered(column.id));

      const filteredColumns = columnsBeforefilter.map((columnGroup) => ({
        ...columnGroup,
        width: filterColumns(columnGroup.columns)?.length * 50,
        columns: filterColumns(columnGroup.columns),
      }));

      setColumnsData([
        userColumn,
        ...filteredColumns
      ]);
    }
  }, [filter]);

  const columns = useMemo(
    () => columnsData,
    [columnsData]
  )

  const defaultColumn = React.useMemo(
    () => ({
      width: 150,
    }),
    []
  )

  const {
    getTableBodyProps,
    headerGroups,
    rows,
    totalColumnsWidth,
    prepareRow,
  } = useTable(
    {
      columns,
      data: chartData,
      defaultColumn,
    },
    useBlockLayout,
    useSortBy
  )

  const generateHeaderStyles = (column) => {
    return {
      style: {
        minWidth: column.width,
        width: column.width,
      },
    }
  }

  const combinedHeaderProps = (defaultHeaderProps, { column }) => {
    return [defaultHeaderProps, generateHeaderStyles(column), column.getSortByToggleProps()]
  }

  const RenderRow = React.useCallback(
    ({ index, style }) => {
      const row = rows[index]
      prepareRow(row)
      return (
        <div
          {...row.getRowProps({
            style,
          })}
          className="tr custom-row"
        >
          {row.cells.map(cell => {
            return (
              <div {...cell.getCellProps()} className="td custom-td">
                {cell.render('Cell')}
              </div>
            )
          })}
        </div>
      )
    },
    [prepareRow, rows]
  )

  return (
    <div className="table-wrapper">
      <section className='scale-box__container'>
        <div className='scale-box'>
          <div className='scale-pill-color' style={{ backgroundColor: '#0b7348' }} />
          <small>80 ~ 100</small>
        </div>
        <div className='scale-box'>
          <div className='scale-pill-color' style={{ backgroundColor: '#42a67c' }} />
          <small>60 ~ 79</small>
        </div>
        <div className='scale-box'>
          <div className='scale-pill-color' style={{ backgroundColor: '#ffcc03' }} />
          <small>40 ~ 59</small>
        </div>
        <div className='scale-box'>
          <div className='scale-pill-color' style={{ backgroundColor: '#ff8c02' }} />
          <small>20 ~ 39</small>
        </div>
        <div className='scale-box'>
          <div className='scale-pill-color' style={{ backgroundColor: '#ff0101' }} />
          <small>0 ~ 19</small>
        </div>
      </section>
      <div className="table heatmaptable">
        <>
          {headerGroups.map(headerGroup => (
            <div {...headerGroup.getHeaderGroupProps()} className="tr column-header">
              {headerGroup.headers.map(column => (
                <div {...column.getHeaderProps(combinedHeaderProps)} className={`th column-header ${column.isSorted
                    ? column.isSortedDesc
                      ? "desc"
                      : "asc"
                    : ""
                  }`}>
                  {column.render('Header')}
                  {column.isSorted ? (
                    column.isSortedDesc ? (
                      <RiArrowDownLine />
                    ) : (
                      <RiArrowUpLine />
                    )
                  ) : (
                    ""
                  )}
                </div>
              ))}
            </div>
          ))}
        </>

        <div {...getTableBodyProps()} className="row-group">
          <FixedSizeList
            height={350}
            itemCount={rows.length}
            itemSize={35}
            width={totalColumnsWidth + 15}
            className={"list-heatmap-scroll-style"}>

            {RenderRow}
          </FixedSizeList>
        </div>
      </div>
    </div>
  )
}
